/**
 * A/B Test Dashboard
 *
 * Main dashboard for the A/B Testing feature within Advanced Simulation.
 * Provides interface for creating, monitoring, and managing A/B tests.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as ExportIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Cancel as CancelIcon,
  EmojiEvents as ConcludeIcon,
  AddCircle as AddRunsIcon,
  ContentCopy as CloneIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import ABTestConfigForm from './ABTestConfigForm';
import ABTestDetailView from './ABTestDetailView';
import ABTestExportModal from './ABTestExportModal';
import {
  listABTests,
  startABTest,
  cancelABTest,
  deleteABTest,
  cloneABTest
} from '../../../services/advancedSimulationApi';

const STATUS_COLORS = {
  draft: 'default',
  running: 'primary',
  completed: 'success',
  concluded: 'info',
  cancelled: 'error'
};

const STATUS_LABELS = {
  draft: 'Draft',
  running: 'Running',
  completed: 'Completed',
  concluded: 'Concluded',
  cancelled: 'Cancelled'
};

const TEST_TYPE_LABELS = {
  prompt_version: 'Prompt Version',
  modality_comparison: 'Modality Comparison'
};

function ABTestDashboard({ token, isResearcher = false }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTestData, setEditTestData] = useState(null);

  // Export modal
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportTest, setExportTest] = useState(null);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        offset: page * rowsPerPage,
        limit: rowsPerPage
      };
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.testType = typeFilter;

      const result = await listABTests(filters, token);
      const testList = result.tests || [];
      setTests(testList);
      // Server doesn't return total count; estimate from results
      setTotal(result.pagination?.total || (testList.length < rowsPerPage ? page * rowsPerPage + testList.length : (page + 2) * rowsPerPage));
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to load A/B tests');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, statusFilter, typeFilter]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const handleViewTest = (testId) => {
    setSelectedTestId(testId);
    setViewDialogOpen(true);
  };

  const handleStartTest = async (testId) => {
    try {
      await startABTest(testId, token);
      fetchTests();
    } catch (err) {
      setError(err.error || 'Failed to start test');
    }
  };

  const handleCancelTest = async (testId) => {
    if (window.confirm('Are you sure you want to cancel this test?')) {
      try {
        await cancelABTest(testId, token);
        fetchTests();
      } catch (err) {
        setError(err.error || 'Failed to cancel test');
      }
    }
  };

  const handleDeleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to delete this test? This cannot be undone.')) {
      try {
        await deleteABTest(testId, token);
        fetchTests();
      } catch (err) {
        setError(err.error || 'Failed to delete test');
      }
    }
  };

  const handleExportTest = (test) => {
    setExportTest(test);
    setExportModalOpen(true);
  };

  const handleTestCreated = (result) => {
    setCreateDialogOpen(false);
    if (result?._id) {
      setSelectedTestId(result._id);
      setViewDialogOpen(true);
    }
    fetchTests();
  };

  const handleEditTest = (test) => {
    setViewDialogOpen(false);
    setEditTestData(test);
    setEditDialogOpen(true);
  };

  const handleTestUpdated = (result) => {
    setEditDialogOpen(false);
    setEditTestData(null);
    fetchTests();
    if (result?._id) {
      setSelectedTestId(result._id);
      setViewDialogOpen(true);
    }
  };

  const handleCloneTest = async (testId) => {
    try {
      const result = await cloneABTest(testId, {}, token);
      const cloned = result.test || result;
      fetchTests();
      if (cloned) {
        handleEditTest(cloned);
      }
    } catch (err) {
      setError(err.error || 'Failed to clone test');
    }
  };

  const filteredTests = tests.filter(test =>
    !searchQuery || test.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeTestCount = tests.filter(t => t.status === 'running').length;

  const getProgress = (test) => {
    if (!test.variants || test.variants.length === 0) return 0;
    const totalExpected = (test.executionConfig?.runsPerVariantPerClient || 5)
      * (test.clientConfig?.clientPersonaKeys?.length || 1)
      * test.variants.length;
    const totalCompleted = test.variants.reduce((sum, v) => sum + (v.stats?.runCount || 0), 0);
    if (totalExpected === 0) return 0;
    return Math.round((totalCompleted / totalExpected) * 100);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">A/B Test Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            Compare prompt versions and therapeutic modalities with statistical rigor
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeTestCount > 0 && (
            <Chip
              label={`${activeTestCount} Active`}
              color="primary"
              variant="outlined"
            />
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New A/B Test
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search tests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="running">Running</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="concluded">Concluded</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Test Type</InputLabel>
            <Select
              value={typeFilter}
              label="Test Type"
              onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="prompt_version">Prompt Version</MenuItem>
              <MenuItem value="modality_comparison">Modality Comparison</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchTests}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredTests.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">No A/B tests found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Variants</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTests.map((test) => (
                  <TableRow key={test._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {test.name}
                      </Typography>
                      {test.hypothesis && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {test.hypothesis.substring(0, 60)}{test.hypothesis.length > 60 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={TEST_TYPE_LABELS[test.testType] || test.testType}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[test.status] || test.status}
                        color={STATUS_COLORS[test.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {test.variants?.length || 0}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getProgress(test)}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(test.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {test.status === 'draft' && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditTest(test)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {test.status === 'draft' && (
                          <Tooltip title="Start">
                            <IconButton size="small" onClick={() => handleStartTest(test._id)} color="primary">
                              <StartIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {['draft', 'running', 'completed', 'concluded', 'cancelled'].includes(test.status) && (
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewTest(test._id)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {test.status === 'running' && (
                          <Tooltip title="Cancel">
                            <IconButton size="small" onClick={() => handleCancelTest(test._id)} color="warning">
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {['completed', 'concluded'].includes(test.status) && (
                          <Tooltip title="Export">
                            <IconButton size="small" onClick={() => handleExportTest(test)}>
                              <ExportIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Clone as Draft">
                          <IconButton size="small" onClick={() => handleCloneTest(test._id)}>
                            <CloneIcon />
                          </IconButton>
                        </Tooltip>
                        {['draft', 'cancelled'].includes(test.status) && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteTest(test._id)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New A/B Test</DialogTitle>
        <DialogContent>
          <ABTestConfigForm
            token={token}
            onTestCreated={handleTestCreated}
            isResearcher={isResearcher}
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => { setViewDialogOpen(false); fetchTests(); }}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent>
          {selectedTestId && (
            <ABTestDetailView
              testId={selectedTestId}
              token={token}
              onClose={() => { setViewDialogOpen(false); fetchTests(); }}
              onExport={(test) => {
                setViewDialogOpen(false);
                handleExportTest(test);
              }}
              onClone={(clonedTest) => {
                fetchTests();
                if (clonedTest) {
                  handleEditTest(clonedTest);
                }
              }}
              onEdit={handleEditTest}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditTestData(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit A/B Test</DialogTitle>
        <DialogContent>
          {editTestData && (
            <ABTestConfigForm
              token={token}
              onTestUpdated={handleTestUpdated}
              isResearcher={isResearcher}
              initialData={editTestData}
              editMode={true}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <ABTestExportModal
        open={exportModalOpen}
        onClose={() => {
          setExportModalOpen(false);
          setExportTest(null);
        }}
        test={exportTest}
        token={token}
      />
    </Box>
  );
}

export default ABTestDashboard;
